import React from "react";
import type { ReactNode } from "react";
import { BrandLogo } from "./BrandLogo";
import { Button } from "./Button";

export interface BrandNavProps {
  logoHref?: string;
  /** Render prop: receives a `close` callback to dismiss the mobile menu on navigation */
  links: (close: () => void) => ReactNode;
  /** Rendered after the desktop links — e.g. UserAvatar */
  trailing?: ReactNode;
  /** Rendered below mobile links with a top border — e.g. social icons */
  mobileExtra?: ReactNode;
}

export const BrandNav: React.FC<BrandNavProps> = ({
  logoHref = "/",
  links,
  trailing,
  mobileExtra,
}) => {
  const [open, setOpen] = React.useState(false);
  const close = () => setOpen(false);

  return (
    <header className="sticky top-0 z-50 bg-gradient-to-tr from-satyrs-dark to-satyrs-dark-alt/80 border-b border-slate-700/60 backdrop-blur py-2 text-white">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4">
        <a
          href={logoHref}
          className="select-none flex-shrink-0 hover:opacity-80 transition-opacity"
        >
          <BrandLogo />
        </a>

        {/* Desktop links + trailing slot */}
        <div className="hidden md:flex items-center gap-1">
          {links(close)}
          {trailing && <div className="ml-2">{trailing}</div>}
        </div>

        {/* Mobile toggle */}
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="brand-mobile-nav"
          className="md:hidden"
        >
          Menu
        </Button>

        {/* Mobile dropdown */}
        {open && (
          <div
            id="brand-mobile-nav"
            className="md:hidden basis-full flex flex-col gap-0.5 mt-2 pb-2"
          >
            {links(close)}
            {mobileExtra && <div className="mt-2 pt-2 border-t border-white/10">{mobileExtra}</div>}
          </div>
        )}
      </div>
    </header>
  );
};
